import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldAlert,
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const SEVERITY_CONFIG = {
  critical: { icon: AlertCircle, color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Critical' },
  warning: { icon: AlertTriangle, color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Warning' },
  info: { icon: Info, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Info' },
};

const ALERT_TYPE_LABELS = {
  NEEDS_REVIEW: 'Needs Review',
  LOW_MAPPING_CONFIDENCE: 'Low Confidence',
  MISSING_SKU_SIZE: 'Missing SKU Size',
  UNRESOLVED_EXTERNAL_ID: 'Unresolved ID',
  INCONSISTENT_UNITS: 'Inconsistent Units',
  XREF_DEPENDENCY: 'XREF Bridge',
  MISSING_UNIT_NORM: 'Missing Unit Norm',
};

const DataQualityAlertsSection = ({ data, loading, onRefresh }) => {
  const { theme } = useTheme();
  const currentColors = colors[theme];
  const [expandedSeverity, setExpandedSeverity] = useState({ critical: true, warning: true, info: false });
  const [expandedAlert, setExpandedAlert] = useState(null);

  if (loading) {
    return (
      <div
        className="rounded-xl p-8 mb-6 flex items-center justify-center"
        style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
        data-testid="data-quality-loading"
      >
        <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: currentColors.accentRed }} />
        <span className="ml-3 text-sm" style={{ color: currentColors.textSecondary }}>
          Scanning data quality...
        </span>
      </div>
    );
  }

  if (!data || !data.alerts?.length) {
    return (
      <div
        className="rounded-xl p-6 mb-6 text-center"
        style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
        data-testid="data-quality-empty"
      >
        <ShieldAlert size={32} className="mx-auto mb-2" style={{ color: currentColors.accentGreen }} />
        <p className="font-medium" style={{ color: currentColors.textPrimary }}>
          No data quality alerts
        </p>
        <p className="text-xs mt-1" style={{ color: currentColors.textSecondary }}>
          All structural checks passed
        </p>
      </div>
    );
  }

  const alertsBySeverity = {
    critical: data.alerts.filter((a) => a.severity === 'critical'),
    warning: data.alerts.filter((a) => a.severity === 'warning'),
    info: data.alerts.filter((a) => a.severity === 'info'),
  };

  return (
    <motion.div className="mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="data-quality-alerts-section">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={20} style={{ color: currentColors.accentRed }} />
          <h2 className="text-lg font-bold" style={{ color: currentColors.textPrimary }}>
            Data Quality Alerts
          </h2>
          <span
            className="px-2 py-0.5 text-xs font-bold rounded-full"
            style={{ background: currentColors.accentRed + '20', color: currentColors.accentRed }}
          >
            {data.summary.total_alerts}
          </span>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 rounded-lg hover:opacity-80"
          style={{ color: currentColors.textSecondary }}
          data-testid="data-quality-refresh-btn"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Summary pills */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {Object.entries(data.summary.by_severity)
          .filter(([, v]) => v > 0)
          .map(([sev, count]) => {
            const config = SEVERITY_CONFIG[sev];
            if (!config) return null;
            return (
              <span
                key={sev}
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ background: config.bg, color: config.color, border: `1px solid ${config.border}` }}
              >
                {config.label}: {count}
              </span>
            );
          })}
        {data.summary.total_items_scanned != null && (
          <span
            className="px-3 py-1 text-xs rounded-full"
            style={{ background: currentColors.background, color: currentColors.textSecondary }}
          >
            {data.summary.total_items_scanned} items scanned
          </span>
        )}
      </div>

      {/* Severity Groups */}
      {['critical', 'warning', 'info'].map((severity) => {
        const alerts = alertsBySeverity[severity];
        if (!alerts.length) return null;
        const config = SEVERITY_CONFIG[severity];
        const expanded = expandedSeverity[severity];
        const SevIcon = config.icon;

        return (
          <div
            key={severity}
            className="rounded-xl mb-3 overflow-hidden"
            style={{
              background: currentColors.cardBg,
              boxShadow: getNeumorphicShadow(theme),
              border: `1px solid ${config.border}`,
            }}
            data-testid={`dq-severity-${severity}`}
          >
            <button
              onClick={() => setExpandedSeverity((s) => ({ ...s, [severity]: !s[severity] }))}
              className="w-full flex items-center justify-between p-3 hover:opacity-90"
              style={{ background: config.bg }}
              data-testid={`dq-toggle-${severity}`}
            >
              <div className="flex items-center gap-2">
                <SevIcon size={16} style={{ color: config.color }} />
                <span className="text-sm font-semibold" style={{ color: config.color }}>
                  {config.label}
                </span>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: config.color + '20', color: config.color }}
                >
                  {alerts.length}
                </span>
              </div>
              {expanded ? (
                <ChevronUp size={16} style={{ color: config.color }} />
              ) : (
                <ChevronDown size={16} style={{ color: config.color }} />
              )}
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="divide-y" style={{ borderColor: currentColors.border }}>
                    {alerts.map((alert, i) => {
                      const alertKey = `${alert.alert_type}-${alert.entity_id}-${i}`;
                      return (
                        <div key={alertKey} className="p-3" data-testid={`dq-alert-${severity}-${alert.alert_type}-${i}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span
                                  className="text-xs px-2 py-0.5 rounded font-medium"
                                  style={{ background: currentColors.background, color: currentColors.textSecondary }}
                                >
                                  {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                                </span>
                                <span className="text-xs" style={{ color: currentColors.textSecondary }}>
                                  {alert.source_engine}
                                </span>
                              </div>
                              <p className="text-sm font-medium" style={{ color: currentColors.textPrimary }}>
                                {alert.title}
                              </p>
                              <p className="text-xs mt-0.5" style={{ color: currentColors.textSecondary }}>
                                {alert.message}
                              </p>
                            </div>
                            {alert.supporting_metrics && (
                              <button
                                onClick={() => setExpandedAlert(expandedAlert === alertKey ? null : alertKey)}
                                className="ml-2 p-1.5 rounded hover:opacity-70 flex-shrink-0"
                                style={{ color: currentColors.textSecondary }}
                                data-testid={`dq-alert-expand-${severity}-${i}`}
                              >
                                {expandedAlert === alertKey ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            )}
                          </div>
                          <AnimatePresence>
                            {expandedAlert === alertKey && alert.supporting_metrics && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-2 p-2.5 rounded-lg text-xs overflow-hidden"
                                style={{ background: currentColors.background }}
                              >
                                {Object.entries(alert.supporting_metrics).map(([k, v]) => (
                                  <div key={k} className="flex justify-between py-0.5">
                                    <span style={{ color: currentColors.textSecondary }}>{k.replace(/_/g, ' ')}</span>
                                    <span className="font-medium ml-4 text-right" style={{ color: currentColors.textPrimary }}>
                                      {Array.isArray(v) ? v.join(', ') : String(v ?? '—')}
                                    </span>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </motion.div>
  );
};

export default DataQualityAlertsSection;
